<template>
  <div>
    <div
      class="pipe-drawer-overlay"
      :style="{ cursor: isPending ? 'progress' : 'crosshair' }"
      @click="handleClick"
      @mousemove="handleMove"
      @contextmenu.prevent="() => commit(points)"
    >
      <svg
        :style="{
          position: 'absolute',
          left: -scroll.x + 'px',
          top: -scroll.y + 'px',
          width: docSize.w + 'px',
          height: docSize.h + 'px',
          pointerEvents: 'none',
        }"
      >
        <line
          v-for="(guide, i) in activeGuides"
          :key="`guide-${i}`"
          :x1="guide.axis === 'x' ? guide.value : 0"
          :y1="guide.axis === 'y' ? guide.value : 0"
          :x2="guide.axis === 'x' ? guide.value : docSize.w"
          :y2="guide.axis === 'y' ? guide.value : docSize.h"
          stroke="#2563eb"
          stroke-width="1"
          stroke-dasharray="4 3"
          stroke-linecap="round"
        />

        <polyline
          v-if="previewPoints.length >= 2"
          :points="previewPoints.map((p) => `${p.x},${p.y}`).join(' ')"
          fill="none"
          stroke="#2563eb"
          stroke-width="3"
          stroke-dasharray="6 4"
          stroke-linecap="round"
          stroke-linejoin="round"
        />

        <circle
          v-for="(p, i) in points"
          :key="`pt-${i}`"
          :cx="p.x"
          :cy="p.y"
          r="5"
          fill="#ffffff"
          stroke="#2563eb"
          stroke-width="2"
        />
      </svg>
    </div>

    <div class="pipe-drawer-hint">
      <div class="pipe-drawer-hint-title">
        Desenhando tubulação ({{ points.length }} pontos)
      </div>
      <div class="pipe-drawer-hint-sub">
        Clique para adicionar ponto · Shift para ângulo livre · Backspace para
        desfazer · Enter ou botão direito para finalizar · Esc para cancelar
      </div>
    </div>
  </div>
</template>

<script>
import {
  ComponenteTipo,
  PipeArrowDirection,
  PipeLineStyle,
} from '@/utils/componente'
import http from '@/utils/http'
import pipeSnapRegistry from '@/utils/pipe-snap-registry'

const SNAP_THRESHOLD = 6
const PIPE_THICKNESS = 12

function applyOrthogonalSnap(last, cursor, freeAngle) {
  if (freeAngle) return cursor
  const dx = cursor.x - last.x
  const dy = cursor.y - last.y
  if (Math.abs(dx) >= Math.abs(dy)) {
    return { x: cursor.x, y: last.y }
  }
  return { x: last.x, y: cursor.y }
}

export default {
  name: 'ViewPipeDrawer',
  props: {
    viewId: {
      type: [String, Number],
      required: true,
    },
    z: {
      type: Number,
      default: 0,
    },
    // Container de referencia (ref="reference") usado para converter coords de
    // pagina para o espaco de coordenadas dos componentes (translate x/y).
    referenceEl: {
      type: null,
      default: null,
    },
  },
  data() {
    return {
      points: [],
      cursor: null,
      shiftHeld: false,
      activeGuides: [],
      isPending: false,
      scroll: { x: window.scrollX, y: window.scrollY },
      docSize: {
        w: document.documentElement.scrollWidth,
        h: document.documentElement.scrollHeight,
      },
    }
  },
  computed: {
    previewPoints() {
      return this.cursor && this.points.length > 0
        ? [...this.points, this.cursor]
        : this.points
    },
  },
  mounted() {
    window.addEventListener('keydown', this.onKeyDown)
    window.addEventListener('keyup', this.onKeyUp)
    window.addEventListener('scroll', this.onScroll, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('keyup', this.onKeyUp)
    window.removeEventListener('scroll', this.onScroll)
  },
  methods: {
    onScroll() {
      this.scroll = { x: window.scrollX, y: window.scrollY }
    },
    onKeyDown(e) {
      const el = e.target
      if (
        el &&
        (el.tagName === 'INPUT' ||
          el.tagName === 'TEXTAREA' ||
          el.isContentEditable)
      ) {
        return
      }
      if (e.key === 'Shift') {
        this.shiftHeld = true
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        this.$emit('cancel')
        return
      }
      if (e.key === 'Enter') {
        e.preventDefault()
        this.commit(this.points)
        return
      }
      if (e.key === 'Backspace') {
        e.preventDefault()
        this.points = this.points.slice(0, -1)
      }
    },
    onKeyUp(e) {
      if (e.key === 'Shift') this.shiftHeld = false
    },
    toPage(e) {
      return { x: e.clientX + window.scrollX, y: e.clientY + window.scrollY }
    },
    applySnap(raw) {
      const ortho =
        this.points.length === 0
          ? raw
          : applyOrthogonalSnap(
              this.points[this.points.length - 1],
              raw,
              this.shiftHeld
            )

      const candidates = [
        ...this.points,
        ...pipeSnapRegistry.getOtherPoints('__drawer__'),
      ]

      let x = ortho.x
      let y = ortho.y
      let bestX = null
      let bestY = null
      for (const other of candidates) {
        const dx = Math.abs(ortho.x - other.x)
        if (dx <= SNAP_THRESHOLD && (!bestX || dx < bestX.dist)) {
          bestX = { dist: dx, value: other.x }
        }
        const dy = Math.abs(ortho.y - other.y)
        if (dy <= SNAP_THRESHOLD && (!bestY || dy < bestY.dist)) {
          bestY = { dist: dy, value: other.y }
        }
      }
      const guides = []
      if (bestX) {
        x = bestX.value
        guides.push({ axis: 'x', value: bestX.value })
      }
      if (bestY) {
        y = bestY.value
        guides.push({ axis: 'y', value: bestY.value })
      }
      return { pos: { x, y }, guides }
    },
    handleClick(e) {
      if (e.button !== 0) return
      const { pos } = this.applySnap(this.toPage(e))
      this.points = [...this.points, pos]
    },
    handleMove(e) {
      const { pos, guides } = this.applySnap(this.toPage(e))
      this.cursor = pos
      this.activeGuides = guides
    },
    async commit(allPoints) {
      if (this.isPending) return
      if (allPoints.length < 2) {
        this.$notify({
          group: 'main',
          title: 'Mensagem',
          type: 'warn',
          text: 'Uma tubulação precisa de pelo menos 2 pontos',
          duration: 5000,
          position: 'bottom right',
        })
        return
      }

      // Converte de coords de pagina para o espaco do container de referencia
      // (mesmo espaco usado pelo translate x/y dos componentes).
      const refRect = this.referenceEl
        ? this.referenceEl.getBoundingClientRect()
        : { left: 0, top: 0 }
      const refOrigin = {
        x: refRect.left + window.scrollX,
        y: refRect.top + window.scrollY,
      }
      const inRef = allPoints.map((p) => ({
        x: p.x - refOrigin.x,
        y: p.y - refOrigin.y,
      }))

      const rawOrigin = inRef[0]
      const rel = inRef.map((p) => ({
        x: p.x - rawOrigin.x,
        y: p.y - rawOrigin.y,
      }))

      // Normaliza para que todas as coords relativas fiquem >= 0, compensando
      // na origem do wrapper. Garante que o bbox cubra todos os vertices.
      const shiftX = Math.min(0, ...rel.map((p) => p.x))
      const shiftY = Math.min(0, ...rel.map((p) => p.y))

      const renderPad = PIPE_THICKNESS / 2 + 12
      const origin = {
        x: rawOrigin.x + shiftX - renderPad,
        y: rawOrigin.y + shiftY - renderPad,
      }

      const value = {
        points: rel.map((p) => ({
          free: { x: p.x - shiftX, y: p.y - shiftY },
        })),
        color: '#6b7280',
        thickness: PIPE_THICKNESS,
        lineStyle: PipeLineStyle.Solid,
        arrow: PipeArrowDirection.None,
        flatColor: true,
        borderColor: '',
        borderThicknessPercent: 20,
      }

      this.isPending = true
      try {
        const componenteId = await http.post('/view/componente', {
          tipo: ComponenteTipo.PipeView,
          viewId: this.viewId,
          value,
        })

        // http.post retorna null em erro (ja exibe o toast). Sem o cano criado,
        // nao emite 'created' — evita fechar o drawer e recarregar como se tivesse
        // dado certo, perdendo o desenho.
        if (!componenteId) return

        // O cano ja existe no backend a partir daqui. Posiciona-o; se este PUT
        // falhar (toast ja exibido), ainda emitimos 'created' para a view refletir
        // o componente que foi de fato criado.
        await http.put('view/componente/position', {
          viewId: this.viewId,
          positions: [
            {
              id: componenteId,
              x: Math.round(origin.x),
              y: Math.round(origin.y),
              z: this.z,
              scaleX: 1,
              scaleY: 1,
            },
          ],
        })

        this.$emit('created')
      } finally {
        this.isPending = false
      }
    },
  },
}
</script>

<style scoped>
.pipe-drawer-overlay {
  position: fixed;
  inset: 0;
  z-index: 9998;
  background-color: rgba(59, 130, 246, 0.04);
}

.pipe-drawer-hint {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  bottom: 24px;
  z-index: 9999;
  pointer-events: none;
  user-select: none;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: rgba(255, 255, 255, 0.95);
  padding: 8px 16px;
  font-size: 14px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.pipe-drawer-hint-title {
  font-weight: 600;
}

.pipe-drawer-hint-sub {
  color: #6b7280;
  font-size: 12px;
}
</style>
